import { Injectable } from '@angular/core';
import { RoleplayService } from 'src/app/pages/evaluacion-practica/services/roleplay.service';

/**
 * La puerta única de "¿se puede dar esta evaluación?" y "¿sigue pendiente?".
 *
 * Un tema puede traer dos evaluaciones independientes —la teórica (`evaluable`
 * + `tipo_ev`) y la práctica (bloque `roleplay`)— y las dos son obligatorias
 * para darlo por completo. Las condiciones vivían como getters de
 * `tema.page.ts`, pero ahora también las necesitan las dos pantallas de
 * resultado para ofrecer el atajo a la evaluación que quedó pendiente, así que
 * se mudan acá: una sola implementación, tres consumidores.
 *
 * Son funciones puras sobre el payload de `temas/v2`: no piden nada. Reciben el
 * tema por parámetro y no lo guardan, porque quien las llama lo reasigna todo
 * el tiempo (cada recarga del curso trae intentos nuevos).
 */
@Injectable({ providedIn: 'root' })
export class EvaluacionesTemaService {
  constructor(private roleplayService: RoleplayService) {}

  // ── Se puede dar ahora mismo ───────────────────────────────────────────────

  /** La teórica del tema se puede dar ahora mismo. */
  puedeDarTeorica(tema): boolean {
    if (tema?.evaluable !== 'si') return false;
    // `> 0` y no `!= 0`: cubre null/undefined además del 0.
    if (!(tema.intentos_restantes > 0)) return false;
    // La open no se repite: mismo criterio que su `[disabled]` de siempre. Con nota
    // (`con_nota`), tampoco mientras está en revisión ni ya aprobada; desaprobada y
    // con intentos, sí (el backend la acepta).
    if (tema.tipo_ev === 'open' && this.abiertaCerrada(tema)) return false;
    return true;
  }

  // ── Abierta con nota ───────────────────────────────────────────────────────

  /**
   * La abierta se califica (gestor o Isa): `realizado` deja de ser «terminada» y
   * pasa a ser «en revisión», y después viene `aprobado` / `desaprobado` como una
   * calificada. Sin `con_nota` (backend viejo o tema sin nota) es la de siempre.
   */
  abiertaConNota(tema): boolean {
    return tema?.tipo_ev === 'open' && !!tema?.con_nota;
  }

  /** Enviada y esperando la nota. */
  abiertaEnRevision(tema): boolean {
    return this.abiertaConNota(tema) && tema?.estado_tema === 'realizado';
  }

  /** La abierta ya no se puede volver a enviar: realizada, o aprobada si lleva nota. */
  abiertaCerrada(tema): boolean {
    if (tema?.estado_tema === 'realizado') return true;
    return this.abiertaConNota(tema) && tema?.estado_tema === 'aprobado';
  }

  /**
   * El roleplay del tema se puede dar ahora mismo: le quedan intentos, el
   * espacio le deja usarlos (`allow_practice_after_pass` con la práctica ya
   * aprobada) y la modalidad corre en esta versión de la app.
   */
  puedeDarPractica(tema): boolean {
    const rp = this.roleplayService.roleplayDeTema(tema);
    return (
      this.roleplayService.puedeIntentar(rp) &&
      this.roleplayService.modoSoportado(rp)
    );
  }

  /** Hay al menos una evaluación que abrir. */
  puedeAbrirEvaluacion(tema): boolean {
    return this.puedeDarTeorica(tema) || this.puedeDarPractica(tema);
  }

  // ── Sigue pendiente ────────────────────────────────────────────────────────

  /**
   * La teórica ya se dio, con el criterio de su propio botón: la `open` con
   * haberla intentado, las demás con haberla aprobado. Una `qualified`
   * desaprobada con intentos **sigue pendiente**, igual que su botón de siempre
   * se queda en primario ("Dar evaluación", no "Mejora tu nota").
   *
   * `mod_evaluaciones` sale del tema y cae al curso, que es la precedencia que
   * arma `tema.page.ts` al elegir tema activo.
   */
  teoricaRealizada(tema, curso?): boolean {
    if (tema?.evaluable !== 'si') return false;

    const modEv = tema?.mod_evaluaciones || curso?.mod_evaluaciones;

    if (tema?.tipo_ev === 'open') {
      // Con nota: en revisión ya no depende del alumno; desaprobada con intentos,
      // sigue pendiente, como una calificada.
      if (this.abiertaConNota(tema)) return this.abiertaCerrada(tema);
      return modEv?.nro_intentos != tema?.intentos_restantes;
    }

    return (
      +tema?.nota >= +tema?.mod_evaluaciones?.nota_aprobatoria ||
      +tema?.nota >= +curso?.mod_evaluaciones?.nota_aprobatoria
    );
  }

  /**
   * La teórica sigue pendiente **y es alcanzable**. Espejo exacto de
   * `pendingPractica`: una teórica sin intentos no cuenta como pendiente —no
   * habría forma de resolverla—, el tema se da por perdido, no por eterno.
   */
  pendingTeorica(tema, curso?): boolean {
    return this.puedeDarTeorica(tema) && !this.teoricaRealizada(tema, curso);
  }

  /**
   * La práctica sigue pendiente, así que el tema no puede darse por completo
   * aunque la teórica ya esté aprobada.
   *
   * "Realizada" se deriva de `attempts`: el contrato del tema no trae un estado
   * propio del roleplay. Un roleplay que **no se puede dar** (sin intentos
   * configurados, o en una modalidad que esta versión de la app no sabe correr)
   * no cuenta como pendiente: si contara, el tema quedaría bloqueado para
   * siempre.
   */
  pendingPractica(tema): boolean {
    const rp = this.roleplayService.roleplayDeTema(tema);
    return !!rp && !this.roleplayService.isDone(rp) && this.puedeDarPractica(tema);
  }
}
